import { useState, useRef, useEffect } from 'react';

/**
 * Custom hook that determines which chart IDs should be active (rendered)
 * based on section expansion state, filter configuration, and chart layout mode (split/combined).
 *
 * It also ensures that global selection ranges are synchronized across all relevant charts.
 *
 * Used in the container where graphs are grouped into sections,
 * with support for feature-based filtering and split/combined views per section.
 *
 * @param sectionConfig - Configuration object mapping section keys to their graph definitions.
 * @param availableSections - List of section keys currently in scope.
 * @param expandedSections - Mapping of section keys to their expanded/collapsed state.
 * @param splitCharts - Mapping of section keys to a boolean indicating whether charts are split by sensor.
 * @param activeFeatures - Mapping of feature IDs to a boolean indicating if that feature is active.
 * @param selectedRanges - Global time/selection ranges with metadata including chartId and globalId.
 * @param globalSelectionId - The current global selection ID to synchronize across charts.
 * @param syncSelectionToChart - A callback to manually sync selection state to a specific chart ID.
 *
 * @returns
 * - activeChartIds: A list of chart IDs to render.
 * - activeChartsRef: A mutable ref to the current list of active chart IDs (useful outside React render cycle).
 */

export function useChartIds({
  sectionConfig,
  availableSections,
  expandedSections,
  splitCharts,
  activeFeatures,
  selectedRanges,
  globalSelectionId,
  syncSelectionToChart,
}: {
  sectionConfig: any;
  availableSections: string[];
  expandedSections: Record<string, boolean>;
  splitCharts: Record<string, boolean>;
  activeFeatures: any;
  selectedRanges: any[];
  globalSelectionId: string | null;
  syncSelectionToChart: (id: string) => void;
}) {
  const [activeChartIds, setActiveChartIds] = useState<string[]>([]);
  const activeChartsRef = useRef<string[]>([]);

  useEffect(() => {
    const chartIds: string[] = [];

    availableSections.forEach((sectionKey) => {
      const isExpanded = expandedSections[sectionKey];
      const isSplit = splitCharts[sectionKey];
      const config = sectionConfig[sectionKey];

      if (!isExpanded || !config) return;

      config.graphs?.forEach((graph: any) => {
        const graphId = graph.id;
        const legend = graph.legend || [];
        const features = graph.features || [];

        const activeFeatureIds = features.filter((f: any) => activeFeatures[f.id]).map((f: any) => f.id);
        if (activeFeatureIds.length === 0 && features.length > 0) return;

        if (isSplit) {
          activeFeatureIds.forEach((featureId: string) => {
            legend.forEach((sensor: any) => {
              if (sensor.displayName) {
                const sensorSlug = sensor.displayName.toLowerCase().replace(/\s+/g, '-');
                chartIds.push(`${featureId}-${sensorSlug}`);
              }
            });
          });
        } else {
          activeFeatureIds.forEach((featureId: string) => {
            chartIds.push(`${featureId}-combined`);
          });
        }
      });
    });

    setActiveChartIds(chartIds);
    activeChartsRef.current = chartIds;
  }, [expandedSections, splitCharts, activeFeatures, sectionConfig, availableSections]);

  useEffect(() => {
    if (globalSelectionId && activeChartIds.length > 0) {
      const missing = activeChartIds.filter(
        (id) => !selectedRanges.some((r) => r.chartId === id && r.globalId === globalSelectionId)
      );
      missing.forEach((id) => syncSelectionToChart(id));
    }
  }, [globalSelectionId, activeChartIds, selectedRanges, syncSelectionToChart]);

  return { activeChartIds, activeChartsRef };
}
